// Camp roster import, admin only.
//
// The panel renders; the RULES live in src/lib/campImport.js, which is the
// tested layer. Anything that decides what may be committed, what a warning
// means, or how an undo report reads belongs there, not here.
//
// TWO THINGS THIS FILE MUST NOT "IMPROVE":
//
//  1. NOTHING IS SENT BY DEFAULT. Both notify toggles start OFF and are posted
//     explicitly on every commit, matching CommitBody's `notify_email = False`
//     / `notify_sms = False` (camp_import.py). SMS additionally needs a second,
//     separate click to arm — the standing rule is that no SMS batch goes out
//     without a per-batch OK.
//  2. THERE IS NO "CREATE NEW" RESOLUTION. commit.py:608-625 honors `skip`, and
//     `link` WITH a user_id, and turns every other value on a review row into
//     `skipped / unresolved_review`. Offering "create" would let an admin
//     resolve a whole review queue, pass the commit gate, and have every one of
//     those rows silently dropped. The options rendered below come from
//     RESOLUTION_ACTIONS so this cannot drift.
//
// NO PII IN LOGS. These rosters carry minors' names, emails, and DOB, and an
// `error_message` from the API can embed a raw email address. It is shown to
// the admin on screen and never passed to console.* or Sentry.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Button from './Button';
import ConfirmModal from './ConfirmModal';
import {
  OUTCOME_LABELS,
  OUTCOME_ORDER,
  RESOLUTION_ACTIONS,
  RESOLUTION_LABELS,
  canCommit,
  confirmationRequired,
  estimateNotify,
  needsResolution,
  notImportedRows,
  parseApiTimestamp,
  resolutionPayload,
  summarize,
  undoSummary,
  warningLabel,
  willImportCount,
} from '../lib/campImport';
import { installEscapeGuard } from '../lib/escapeGuard';
import './CampImportPanel.css';

const POLL_MS = 2000;
// ~5 minutes. commit_batch never writes status="failed" — a worker recycled
// between the 202 and the background task leaves the batch at "committing"
// forever (camp_import.py's resume=true exists for exactly that), so the poller
// has to give up and say so rather than spin for the life of the page.
const POLL_LIMIT = 150;
const PARSED_PREVIEW_ROWS = 5;

// Rendered from the module's list, so a future backend that honors another
// action shows up here by editing one file. See the header note.
const OFFER_LINK = RESOLUTION_ACTIONS.includes('link');
const OFFER_SKIP = RESOLUTION_ACTIONS.includes('skip');

// Outcomes commit has yet to resolve. A row that has been processed either
// leaves this set (skipped / error) or gains a user_id.
const UNPROCESSED_OUTCOMES = ['create', 'reuse', 'review'];

const BATCH_STATUS_LABELS = {
  preview: 'Preview only — never committed',
  committing: 'Importing…',
  committed: 'Imported',
  failed: 'Failed',
  undone: 'Undone',
};

const authConfig = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

// Never returns an object: a structured `detail` rendered as a React child
// crashes the page (React error #31), the same trap src/lib/apiError.js exists
// for.
function apiDetail(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const first = detail.find((d) => d && typeof d.msg === 'string');
    if (first) return first.msg;
  }
  if (detail && typeof detail === 'object' && typeof detail.message === 'string') {
    return detail.message;
  }
  return fallback;
}

function saveBlob(data, filename, type) {
  const url = window.URL.createObjectURL(new Blob([data], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

function fmtWhen(value) {
  const parsed = parseApiTimestamp(value);
  return parsed ? parsed.toLocaleString() : '';
}

export default function CampImportPanel({ camp, onImported }) {
  const { API_URL } = useAuth();
  const campId = camp?.id;
  const base = `${API_URL}/camps/${campId}/import`;

  // idle | previewing | preview | committing | done
  const [phase, setPhase] = useState('idle');
  const [chosenFile, setChosenFile] = useState(null);
  const [defaultSport, setDefaultSport] = useState('');
  const [validSports, setValidSports] = useState([]);
  const [preview, setPreview] = useState(null);
  const [resolutions, setResolutions] = useState({});
  const [notifyEmail, setNotifyEmail] = useState(false);
  const [notifySms, setNotifySms] = useState(false);
  const [smsArming, setSmsArming] = useState(false);
  const [typedCount, setTypedCount] = useState('');
  const [error, setError] = useState('');
  // IN-FLIGHT GUARD FOR COMMIT. `phase` does not flip to 'committing' until the
  // POST resolves, so without this both clicks of a double-click read
  // phase === 'preview', both pass the gate, and both schedule. The server's
  // claim refuses the second one — but the two requests race in the window
  // before the claim is written, and concurrent commit_row runs flip good rows
  // to `error`.
  const [submitting, setSubmitting] = useState(false);

  const [pollBatchId, setPollBatchId] = useState(null);
  const [pollStalled, setPollStalled] = useState(false);
  const [progress, setProgress] = useState(null);
  // Kept apart from `error` on purpose. A blip while polling must clear itself
  // on the next good tick — otherwise "Lost contact…" sits above "Import
  // finished. 53 accounts created…" and the panel contradicts itself — and
  // clearing the shared `error` every 2s would wipe unrelated messages the
  // admin has not read yet.
  const [pollError, setPollError] = useState('');

  const [batches, setBatches] = useState([]);
  const [pending, setPending] = useState({});
  const [historyError, setHistoryError] = useState('');
  const [busyBatchId, setBusyBatchId] = useState(null);
  const [armedSmsBatchId, setArmedSmsBatchId] = useState(null);
  const [undoReports, setUndoReports] = useState({});
  const [notice, setNotice] = useState('');
  // The batch awaiting an undo confirmation. ConfirmModal rather than
  // window.confirm, which is the page's pattern for a destructive action.
  const [confirmUndo, setConfirmUndo] = useState(null);
  // Raised once when a commit finishes with rows that did not import. The
  // download is the point, so it is the primary action rather than a link
  // buried in batch history the admin has no reason to open.
  const [notImported, setNotImported] = useState(null);

  const fileInputRef = useRef(null);

  // Held in a ref so the polling effect does not restart every time the parent
  // re-renders with a fresh arrow function — and so exhaustive-deps stays happy
  // without an inline disable, which hard-fails the CRA production build.
  const onImportedRef = useRef(onImported);
  useEffect(() => { onImportedRef.current = onImported; });

  const loadBatches = useCallback(async () => {
    if (!campId) return;
    try {
      const res = await axios.get(`${base}/batches`, authConfig());
      const list = Array.isArray(res.data) ? res.data : [];
      setBatches(list);
      setHistoryError('');
      // `pending` is only meaningful for a committed batch that has not been
      // rolled back; notify refuses to send for anything else.
      const live = list.filter((b) => b.status === 'committed' && !b.undone_at);
      const details = await Promise.all(live.map((b) => (
        axios.get(`${base}/batches/${b.id}`, authConfig())
          .then((r) => [b.id, r.data?.pending || { email: 0, sms: 0 }])
          .catch(() => null)
      )));
      const next = {};
      details.forEach((entry) => { if (entry) next[entry[0]] = entry[1]; });
      setPending(next);
    } catch (e) {
      setHistoryError(apiDetail(e, 'Could not load past imports.'));
    }
  }, [base, campId]);

  useEffect(() => { loadBatches(); }, [loadBatches]);

  // A different camp is a different import. Never carry a preview across.
  useEffect(() => {
    setPhase('idle');
    setChosenFile(null);
    setPreview(null);
    setResolutions({});
    setNotifyEmail(false);
    setNotifySms(false);
    setSmsArming(false);
    setTypedCount('');
    setError('');
    setProgress(null);
    setPollBatchId(null);
    setPollStalled(false);
    setPollError('');
    setUndoReports({});
    setNotice('');
    setConfirmUndo(null);
  }, [campId]);

  // While the undo confirmation is open, Escape must dismiss THAT and nothing
  // else. CampsPage.js:425-431 keeps an unconditional document listener that
  // runs `setRegsCamp(null); setShowImport(false)` for as long as the
  // registrations modal is open, and ConfirmModal.js:5-9 adds its own without
  // stopping propagation — so without this guard one Escape would cancel the
  // undo AND unmount this panel, throwing away an uncommitted preview, the
  // review decisions made on it, or the live view of a commit still running on
  // the server. The guard swallows the key (see escapeGuard.js) and therefore
  // has to close the dialog itself.
  useEffect(() => {
    if (!confirmUndo) return undefined;
    return installEscapeGuard(() => setConfirmUndo(null));
  }, [confirmUndo]);

  // Commit is a 202 plus background work, so progress has to be pulled.
  useEffect(() => {
    if (!pollBatchId) return undefined;
    let cancelled = false;
    let timer = null;
    let attempts = 0;

    const tick = async () => {
      attempts += 1;
      try {
        const res = await axios.get(`${base}/batches/${pollBatchId}`, authConfig());
        if (cancelled) return;
        setProgress(res.data);
        // The tick that worked retires the last tick that did not.
        setPollError('');
        const status = res.data?.status;
        if (status === 'committed' || status === 'failed' || status === 'undone') {
          setPollBatchId(null);
          setPhase('done');
          if (status === 'committed') {
            const missed = notImportedRows(res.data?.rows);
            if (missed > 0) {
              setNotImported({ batchId: pollBatchId, count: missed,
                               total: (res.data?.rows || []).length });
            }
          }
          loadBatches();
          if (onImportedRef.current) onImportedRef.current();
          return;
        }
      } catch (e) {
        if (cancelled) return;
        // Transient: keep polling. The import is running on the server either
        // way, and giving up here would only hide it.
        setPollError(apiDetail(e, 'Lost contact with the import while checking on it.'));
      }
      if (cancelled) return;
      if (attempts >= POLL_LIMIT) {
        setPollBatchId(null);
        setPollStalled(true);
        return;
      }
      timer = window.setTimeout(tick, POLL_MS);
    };

    tick();
    return () => { cancelled = true; if (timer) window.clearTimeout(timer); };
  }, [pollBatchId, base, loadBatches]);

  const downloadTemplate = async (format) => {
    setError('');
    try {
      const res = await axios.get(`${base}/template?format=${format}`,
        { ...authConfig(), responseType: 'blob' });
      saveBlob(res.data, `camp_roster_template.${format}`,
        format === 'xlsx'
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'text/csv');
    } catch (e) {
      setError(apiDetail(e, 'Template download failed.'));
    }
  };

  const downloadErrors = async (batchId) => {
    setError('');
    try {
      const res = await axios.get(`${base}/batches/${batchId}/errors.csv`,
        { ...authConfig(), responseType: 'blob' });
      saveBlob(res.data, `ProZpects_import_${batchId}_not_imported.csv`, 'text/csv');
    } catch (e) {
      setError(apiDetail(e, 'Could not download the error rows.'));
    }
  };

  const runPreview = async () => {
    if (!chosenFile) {
      setError('Choose a roster file first.');
      return;
    }
    setError('');
    setNotice('');
    setPhase('previewing');
    const form = new FormData();
    form.append('file', chosenFile);
    if (defaultSport) form.append('default_sport', defaultSport);
    try {
      const res = await axios.post(`${base}/preview`, form, authConfig());
      setPreview(res.data);
      setValidSports(res.data?.valid_sports || []);
      // A new sheet is a new decision on every send.
      setResolutions({});
      setNotifyEmail(false);
      setNotifySms(false);
      setSmsArming(false);
      setTypedCount('');
      setPhase('preview');
    } catch (e) {
      setPreview(null);
      setPhase('idle');
      // The 5 MB cap is enforced at the proxy (`request_body max_size 5MB`),
      // which answers with no JSON body at all, so the endpoint's own 413
      // detail never arrives. Say it here rather than showing the generic
      // failure for the one error an admin can actually act on.
      setError(e?.response?.status === 413
        ? 'That file is larger than 5 MB. Split the roster and import it in parts.'
        : apiDetail(e, 'The roster could not be previewed.'));
    }
  };

  const discardPreview = () => {
    setPreview(null);
    setResolutions({});
    setNotifyEmail(false);
    setNotifySms(false);
    setSmsArming(false);
    setTypedCount('');
    setPhase('idle');
  };

  const startOver = () => {
    discardPreview();
    setChosenFile(null);
    setProgress(null);
    setPollStalled(false);
    setPollError('');
    setError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const runCommit = async () => {
    if (!preview || submitting) return;
    setError('');
    setNotice('');
    setSubmitting(true);
    try {
      const res = await axios.post(`${base}/commit`, {
        batch_id: preview.batch_id,
        resolutions: resolutionPayload(resolutions),
        // Always both, always explicit — the batch's stored flags are written
        // from exactly these two booleans.
        notify_email: notifyEmail,
        notify_sms: notifySms,
      }, authConfig());
      setProgress(null);
      setPollStalled(false);
      setPollError('');
      setPhase('committing');
      setPollBatchId(res.data?.batch_id || preview.batch_id);
    } catch (e) {
      setError(apiDetail(e, 'The import could not be started.'));
    } finally {
      // Released on both paths. A failed commit must stay retryable; a
      // successful one has already left the preview phase.
      setSubmitting(false);
    }
  };

  const sendLater = async (batchId, channels) => {
    setError('');
    setBusyBatchId(batchId);
    try {
      await axios.post(`${base}/batches/${batchId}/notify`, channels, authConfig());
      setNotice(channels.sms
        ? 'Texting started. Only people who have not been messaged yet are included.'
        : 'Sending started. Only people who have not been emailed yet are included.');
      setArmedSmsBatchId(null);
      await loadBatches();
    } catch (e) {
      setError(apiDetail(e, 'Could not start sending.'));
    } finally {
      setBusyBatchId(null);
    }
  };

  const runUndo = async (batch) => {
    setConfirmUndo(null);
    setError('');
    setBusyBatchId(batch.id);
    try {
      const res = await axios.post(`${base}/batches/${batch.id}/undo`, {}, authConfig());
      const summary = undoSummary(res.data);
      setUndoReports((prev) => ({
        ...prev,
        // undoSummary returns null for anything that is not an object. The
        // report IS the operation here, so say that it happened and cannot be
        // read rather than rendering nothing at all.
        [batch.id]: summary ? { summary } : {
          error: 'Undo ran, but its report could not be read. Refresh to see '
            + "the batch's current state.",
        },
      }));
      await loadBatches();
    } catch (e) {
      setUndoReports((prev) => ({
        ...prev,
        [batch.id]: { error: apiDetail(e, 'Undo failed.') },
      }));
    } finally {
      setBusyBatchId(null);
    }
  };

  const setResolution = (rowNumber, choice) => {
    setResolutions((prev) => ({ ...prev, [rowNumber]: choice }));
  };

  const clearResolution = (rowNumber) => {
    setResolutions((prev) => {
      const next = { ...prev };
      delete next[rowNumber];
      return next;
    });
  };

  if (!campId) return null;

  const rows = preview?.rows || [];
  const summary = summarize(rows);
  const reviewRows = needsResolution(rows);
  const resolvedCount = Object.keys(resolutionPayload(resolutions)).length;
  const estimate = estimateNotify(rows, resolutions);
  // What a commit RIGHT NOW would write. summary.importable is the ceiling and
  // counts review rows the admin chose to skip, so a two-sibling file offered
  // to "Import 2 rows" and imported none — and asked for the wrong number in
  // the >100 confirmation.
  const willImport = willImportCount(rows, resolutions);
  const needsTyped = confirmationRequired(willImport);
  const typedOk = !needsTyped || typedCount.trim() === String(willImport);
  const commitReady = canCommit(preview, resolutions) && typedOk && !submitting;

  const progressRows = progress?.rows || [];
  const finishedRows = progressRows.filter(
    (r) => !UNPROCESSED_OUTCOMES.includes(r.outcome) || r.user_id).length;
  const progressTotal = progress?.row_count || progressRows.length;
  const doneSummary = progress ? summarize(progressRows) : null;

  return (
    <section className="cip" role="region" aria-label="Camp roster import">
      <div className="cip__head">
        <h3 className="cip__title">Roster import</h3>
        <div className="cip__actions">
          <Button variant="ghost" size="sm" onClick={() => downloadTemplate('csv')}>
            Template (CSV)
          </Button>
          <Button variant="ghost" size="sm" onClick={() => downloadTemplate('xlsx')}>
            Template (XLSX)
          </Button>
        </div>
      </div>

      {error && <div className="cip__alert cip__alert--error" role="alert">{error}</div>}
      {notice && <div className="cip__alert cip__alert--info">{notice}</div>}

      {/* ── upload ───────────────────────────────────────── */}
      {(phase === 'idle' || phase === 'previewing' || phase === 'preview') && (
        <div className="cip__upload">
          <div className="cip__field">
            <label className="cip__label" htmlFor="cip-file">Roster file</label>
            <input
              id="cip-file"
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xlsm"
              onChange={(e) => setChosenFile(e.target.files?.[0] || null)}
            />
          </div>
          <div className="cip__field">
            <label className="cip__label" htmlFor="cip-sport">Default sport</label>
            <select
              id="cip-sport"
              value={defaultSport}
              onChange={(e) => setDefaultSport(e.target.value)}
            >
              <option value="">— none (Sport column required) —</option>
              {validSports.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>
          <Button
            variant="primary"
            onClick={runPreview}
            disabled={phase === 'previewing' || !chosenFile}
            loading={phase === 'previewing'}
            loadingText="Reading the sheet…"
          >
            {preview ? 'Re-check file' : 'Check file'}
          </Button>
          {preview && (
            <Button variant="ghost" onClick={discardPreview}>Discard</Button>
          )}
        </div>
      )}
      {validSports.length === 0
        && (phase === 'idle' || phase === 'previewing' || phase === 'preview') && (
        <p className="cip__hint">
          The sport list fills in once a file has been checked. Until then, every
          row needs its own Sport column — the XLSX template carries the full
          list as a dropdown.
        </p>
      )}

      {/* ── preview ──────────────────────────────────────── */}
      {phase === 'preview' && preview && (
        <div className="cip__preview">
          <div className="cip__counts">
            {OUTCOME_ORDER.map((outcome) => (
              <div
                key={outcome}
                className={`cip__count cip__count--${outcome}${summary[outcome] ? '' : ' cip__count--zero'}`}
              >
                <span className="cip__count-n">{summary[outcome]}</span>
                <span className="cip__count-label">{OUTCOME_LABELS[outcome]}</span>
              </div>
            ))}
            <div className="cip__count cip__count--total">
              <span className="cip__count-n">{willImport}</span>
              <span className="cip__count-label">Will be imported</span>
            </div>
          </div>

          <p className="cip__meta">
            {preview.row_count} rows read from
            {preview.sheet_name ? ` sheet “${preview.sheet_name}”` : ' the file'}
            {preview.default_sport ? ` · default sport ${preview.default_sport}` : ''}
            {' · nothing has been written yet.'}
          </p>

          {(preview.batch_warnings || []).map((text) => (
            <div key={text} className="cip__alert cip__alert--warn">{warningLabel(text)}</div>
          ))}

          {(preview.unmatched_headers || []).length > 0 && (
            <div className="cip__alert cip__alert--warn">
              Columns that were not recognised and will be ignored:{' '}
              {preview.unmatched_headers.join(', ')}
            </div>
          )}

          {/* Parsed preview: the fastest way to spot a column mapped to the
              wrong field before anything is written. */}
          <h4 className="cip__subtitle">How the first rows were read</h4>
          <div className="cip__scroll">
            <table className="cip__table">
              <thead>
                <tr>
                  <th>Row</th><th>First name</th><th>Last name</th><th>Email</th><th>Sport</th>
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, PARSED_PREVIEW_ROWS).map((row) => (
                  <tr key={row.row_number}>
                    <td>{row.row_number}</td>
                    <td>{row.first_name || <span className="cip__blank">—</span>}</td>
                    <td>{row.last_name || <span className="cip__blank">—</span>}</td>
                    <td>{row.email || <span className="cip__blank">—</span>}</td>
                    <td>{row.sport || <span className="cip__blank">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {reviewRows.length > 0 && (
            <div className="cip__alert cip__alert--warn">
              {/* TWO different situations reach `review`, and the old copy
                   described only one of them. classify.mark_in_file_duplicates
                   flips BOTH siblings in a sheet to review with
                   candidate_user_ids = [] — they were `create` rows, so no
                   account exists to link to — and the panel then renders Skip
                   as the only button under a sentence telling the admin to
                   link. */}
              {reviewRows.length} row{reviewRows.length === 1 ? '' : 's'} need a
              decision before this can be imported ({resolvedCount} of{' '}
              {reviewRows.length} decided). A row that shares an email or phone
              with an athlete already in ProZpects offers a Link button naming
              that account — use it if it is the same person, otherwise skip the
              row. A row that shares a contact with another row in THIS file has
              no account to link to yet and can only be skipped; to import both,
              give each of them their own email or phone and upload again.
            </div>
          )}

          <h4 className="cip__subtitle">Every row</h4>
          <div className="cip__scroll">
            <table className="cip__table">
              <thead>
                <tr>
                  <th>Row</th>
                  <th>Athlete</th>
                  <th>Email</th>
                  <th>Outcome</th>
                  <th>Notes</th>
                  <th>Decision</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const choice = resolutions[row.row_number];
                  return (
                    <tr key={row.row_number} className={`cip__row cip__row--${row.outcome}`}>
                      <td>{row.row_number}</td>
                      <td>{`${row.first_name || ''} ${row.last_name || ''}`.trim() || '—'}</td>
                      <td>{row.email || '—'}</td>
                      <td>
                        <span className={`cip__chip cip__chip--${row.outcome}`}>
                          {OUTCOME_LABELS[row.outcome] || row.outcome}
                        </span>
                      </td>
                      <td>
                        {/* error_message can carry a raw email address. Shown
                            to the admin, never logged. */}
                        {row.error_message && (
                          <div className="cip__note cip__note--error">{row.error_message}</div>
                        )}
                        {!row.error_message && row.error_code && (
                          <div className="cip__note cip__note--error">{row.error_code}</div>
                        )}
                        {(row.warnings || []).map((code) => (
                          <div key={code} className="cip__note">{warningLabel(code)}</div>
                        ))}
                      </td>
                      <td>
                        {row.outcome === 'review' && (
                          <div className="cip__decide">
                            {/* NAMED, not a bare id. On a shared-inbox review
                                 row the candidate is by construction an athlete
                                 with a DIFFERENT name — the one account that
                                 must not receive this registration — and
                                 "Link to #1953" asked the admin to make that
                                 call blind. candidate_names is parallel to
                                 candidate_user_ids (camp_import.py _row_out). */}
                            {OFFER_LINK && (row.candidate_user_ids || []).map((uid, i) => {
                              const name = (row.candidate_names || [])[i] || `#${uid}`;
                              const picked = choice?.action === 'link' && choice.userId === uid;
                              return (
                                <Button
                                  key={uid}
                                  size="sm"
                                  variant={picked ? 'primary' : 'secondary'}
                                  aria-pressed={picked}
                                  title={RESOLUTION_LABELS.link}
                                  aria-label={`Link row ${row.row_number} to ${name}, account #${uid}`}
                                  onClick={() => setResolution(row.row_number,
                                    { action: 'link', userId: uid })}
                                >
                                  {`Link to ${name} (#${uid})`}
                                </Button>
                              );
                            })}
                            {OFFER_SKIP && (
                              <Button
                                size="sm"
                                variant={choice?.action === 'skip' ? 'primary' : 'secondary'}
                                aria-pressed={choice?.action === 'skip'}
                                title={RESOLUTION_LABELS.skip}
                                aria-label={`Skip row ${row.row_number}`}
                                onClick={() => setResolution(row.row_number, { action: 'skip' })}
                              >
                                Skip
                              </Button>
                            )}
                            {choice && (
                              <Button
                                size="sm"
                                variant="ghost"
                                aria-label={`Clear the decision for row ${row.row_number}`}
                                onClick={() => clearResolution(row.row_number)}
                              >
                                Clear
                              </Button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* ── notify ─────────────────────────────────── */}
          <h4 className="cip__subtitle">Messages</h4>
          <div className="cip__notify">
            <label className="cip__check">
              <input
                type="checkbox"
                checked={notifyEmail}
                onChange={(e) => setNotifyEmail(e.target.checked)}
              />
              {/* "about", not a bare count: estimateNotify works from the
                  SHEET's addresses and the server dedupes on the committed
                  ACCOUNT's, so a row matched by phone with a blank Email column
                  is one more message than this number. See the estimate's
                  comment in src/lib/campImport.js. */}
              <span>{`Email these attendees (about ${estimate.email} recipients)`}</span>
            </label>

            <label className="cip__check">
              <input
                type="checkbox"
                checked={notifySms}
                onChange={(e) => {
                  if (e.target.checked) {
                    // Arm only. The box stays clear until the count below is
                    // acknowledged — SMS never goes out on one click.
                    setSmsArming(true);
                  } else {
                    setNotifySms(false);
                    setSmsArming(false);
                  }
                }}
              />
              <span>{`Text phone-only attendees (up to ${estimate.sms} messages)`}</span>
            </label>

            {smsArming && !notifySms && (
              <div className="cip__arm" role="group" aria-label="Confirm texting">
                <span>
                  {`This texts up to ${estimate.sms} phone-only ${estimate.sms === 1 ? 'family' : 'families'} `}
                  as soon as the import finishes. Texts cannot be recalled.
                </span>
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => { setNotifySms(true); setSmsArming(false); }}
                >
                  {`Yes, text ${estimate.sms}`}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setSmsArming(false)}>
                  Cancel
                </Button>
              </div>
            )}

            <p className="cip__hint">
              Nothing is sent unless you tick a box. You can send invites later
              from the batch list.
            </p>
          </div>

          {needsTyped && (
            <div className="cip__field cip__confirm">
              <label className="cip__label" htmlFor="cip-confirm">
                {`Type ${willImport} to confirm this import`}
              </label>
              <input
                id="cip-confirm"
                type="text"
                inputMode="numeric"
                value={typedCount}
                onChange={(e) => setTypedCount(e.target.value)}
              />
            </div>
          )}

          <div className="cip__commit">
            <Button variant="primary" onClick={runCommit} disabled={!commitReady}>
              {submitting ? 'Starting…' : `Import ${willImport} rows`}
            </Button>
            {!commitReady && !submitting && (
              <span className="cip__hint">
                {willImport === 0
                  ? (summary.importable === 0
                    ? 'Nothing in this file can be imported.'
                    : 'Every row that could be imported has been skipped.')
                  : (reviewRows.length > resolvedCount
                    ? 'Decide every row that needs review first.'
                    : 'Type the number above to confirm.')}
              </span>
            )}
          </div>
        </div>
      )}

      {/* ── progress ─────────────────────────────────────── */}
      {(phase === 'committing' || phase === 'done') && (
        <div className="cip__progress" aria-live="polite">
          {phase === 'committing' && (
            <p>
              {`Importing… ${finishedRows} of ${progressTotal || '?'} rows finished.`}
              {' You can leave this open; the import runs on the server.'}
            </p>
          )}
          {pollError && (
            <p className="cip__alert cip__alert--warn">{pollError}</p>
          )}
          {pollStalled && (
            <p className="cip__alert cip__alert--warn">
              Still importing after several minutes. It may still be running —
              close and reopen this panel to check its status.
            </p>
          )}
          {phase === 'done' && doneSummary && (
            <>
              <p>
                {progress?.status === 'committed'
                  ? `Import finished. ${doneSummary.create} accounts created, `
                    + `${doneSummary.reuse} matched to existing accounts, `
                    + `${doneSummary.skipped} skipped, ${doneSummary.error} errored.`
                  : `Import ended with status “${progress?.status}”.`}
              </p>
              {(doneSummary.error > 0 || doneSummary.conflict > 0
                || doneSummary.dup_in_file > 0) && (
                <Button size="sm" variant="secondary"
                  onClick={() => downloadErrors(progress.id)}>
                  Download the rows that did not import
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={startOver}>
                Import another roster
              </Button>
            </>
          )}
        </div>
      )}

      {/* ── history ──────────────────────────────────────── */}
      <div className="cip__history">
        <div className="cip__head">
          <h4 className="cip__subtitle">Past imports</h4>
          <Button variant="ghost" size="sm" onClick={loadBatches}>Refresh</Button>
        </div>
        {historyError && <div className="cip__alert cip__alert--error">{historyError}</div>}
        {batches.length === 0 && !historyError && (
          <p className="cip__hint">No rosters have been imported for this camp yet.</p>
        )}
        {batches.map((batch) => {
          const counts = batch.counts || {};
          const waiting = pending[batch.id] || { email: 0, sms: 0 };
          const report = undoReports[batch.id];
          const busy = busyBatchId === batch.id;
          const canSend = batch.status === 'committed' && !batch.undone_at;
          return (
            <div key={batch.id} className="cip__batch">
              <div className="cip__batch-head">
                <span className="cip__batch-name">{batch.filename}</span>
                <span className={`cip__chip cip__chip--status-${batch.status}`}>
                  {BATCH_STATUS_LABELS[batch.status] || batch.status}
                </span>
                <span className="cip__batch-when">{fmtWhen(batch.created_at)}</span>
              </div>
              <div className="cip__batch-counts">
                {`${batch.row_count} rows · ${counts.created || 0} created · `}
                {`${counts.reused || 0} existing · ${counts.linked || 0} linked · `}
                {`${counts.skipped || 0} skipped · ${counts.conflict || 0} blocked · `}
                {`${counts.error || 0} errors`}
                {batch.undone_at ? ` · undone ${fmtWhen(batch.undone_at)}` : ''}
              </div>
              <div className="cip__batch-actions">
                {/* The endpoint serves error | conflict | dup_in_file
                     (camp_import.py download_errors), so gating on `error`
                     alone hid the button for a batch whose only failures were
                     conflicts — and once the in-session preview is gone, the
                     history entry is the only way back to those rows.
                     dup_in_file is already folded into count_error. */}
                {((counts.error || 0) + (counts.conflict || 0)) > 0 && (
                  <Button size="sm" variant="ghost" onClick={() => downloadErrors(batch.id)}>
                    Errors CSV
                  </Button>
                )}
                {canSend && waiting.email > 0 && (
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={busy}
                    onClick={() => sendLater(batch.id, { email: true })}
                  >
                    {`Send invites (${waiting.email})`}
                  </Button>
                )}
                {canSend && waiting.sms > 0 && armedSmsBatchId !== batch.id && (
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={busy}
                    onClick={() => setArmedSmsBatchId(batch.id)}
                  >
                    {`Text phone-only (${waiting.sms})`}
                  </Button>
                )}
                {canSend && waiting.sms > 0 && armedSmsBatchId === batch.id && (
                  <span className="cip__arm" role="group" aria-label="Confirm texting">
                    <span>{`Text ${waiting.sms} phone-only ${waiting.sms === 1 ? 'family' : 'families'} now? Texts cannot be recalled.`}</span>
                    <Button
                      size="sm"
                      variant="danger"
                      disabled={busy}
                      onClick={() => sendLater(batch.id, { sms: true })}
                    >
                      {`Yes, text ${waiting.sms}`}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setArmedSmsBatchId(null)}>
                      Cancel
                    </Button>
                  </span>
                )}
                {batch.status === 'committed' && !batch.undone_at && (
                  <Button
                    size="sm"
                    variant="danger"
                    disabled={busy}
                    onClick={() => setConfirmUndo(batch)}
                  >
                    Undo
                  </Button>
                )}
              </div>

              {report?.error && (
                <div className="cip__alert cip__alert--error">{report.error}</div>
              )}
              {report?.summary && (
                <div className={`cip__undo cip__undo--${report.summary.state}`}>
                  <div className="cip__undo-headline">{report.summary.headline}</div>
                  <div className="cip__batch-counts">
                    {`${report.summary.counts.removed} accounts removed · `}
                    {`${report.summary.counts.registrationsRemoved} registrations removed · `}
                    {`${report.summary.counts.guardiansRemoved} guardians removed`}
                  </div>
                  {report.summary.notes.map((note) => (
                    <div key={note} className="cip__note">{note}</div>
                  ))}
                  {report.summary.skipped.length > 0 && (
                    <ul className="cip__undo-list">
                      {report.summary.skipped.map((entry) => (
                        // One row can appear twice — once for the athlete and
                        // once for the guardian it minted (undo.py's `kind`).
                        <li key={`${entry.row_number}-${entry.kind}-${entry.user_id}`}>
                          {`Row ${entry.row_number}${entry.kind === 'guardian' ? ' (guardian)' : ''}: `}
                          {entry.reasonLabels.join(' ')}
                        </li>
                      ))}
                    </ul>
                  )}
                  {report.summary.canRetry && (
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={busy}
                      onClick={() => setConfirmUndo(batch)}
                    >
                      Try undo again
                    </Button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {notImported && (
        <ConfirmModal
          title={`${notImported.count} of ${notImported.total} rows did not import`}
          message={'Download the list to see which athletes were left out and '
            + 'what each one needs. Rows that shared an email with a different '
            + 'athlete are in there — those are the ones worth acting on. The '
            + 'file stays available from this batch in the history below.'}
          confirmLabel="Download the list"
          cancelLabel="Not now"
          onConfirm={() => { downloadErrors(notImported.batchId); setNotImported(null); }}
          onCancel={() => setNotImported(null)}
        />
      )}

      {confirmUndo && (
        <ConfirmModal
          title="Undo this import?"
          message={`“${confirmUndo.filename}”: accounts this import created are `
            + 'deleted, along with the registrations it added. Anyone who has '
            + 'already logged in, set a password, checked in, or had results '
            + 'recorded is kept and reported back. Messages that have already '
            + 'been sent cannot be recalled.'}
          confirmLabel="Undo import"
          danger
          onConfirm={() => runUndo(confirmUndo)}
          onCancel={() => setConfirmUndo(null)}
        />
      )}
    </section>
  );
}
